/**
 * Pure channel-attribution rules — NGE-10856.
 *
 * Extracted from CampaignsAndChannels so the rules can be exercised across a
 * SEQUENCE of pageviews (`npm test`). Fault 2 — a campaign tier that renews
 * itself forever — is invisible on any single page load, so a test that cannot
 * model "one tagged visit, then untagged browsing two hours later" cannot catch
 * it. These functions are the shipped rules, not a copy of them.
 *
 * `.mjs` on purpose: `node --test` imports this file directly and the repo's
 * package.json has no `"type": "module"`, so a `.js` sibling would be parsed as
 * CommonJS and fail on `export`. Rollup resolves the explicit extension as-is.
 */

/**
 * Lowercase every key of the channel → tier index so a `?ch=CRM` and an index
 * authored as `Crm` still meet.
 * @param {Object} index - The shop-level channel → tier index metafield
 * @returns {Object}
 */
export function normaliseIndex(index) {
  const table = {};
  for (const [key, value] of Object.entries(index || {})) {
    table[String(key).toLowerCase()] = value;
  }
  return table;
}

/**
 * Resolve a channel handle to its tier index.
 *
 * Tier 0 is a REAL tier (the default / SEO price list), so it must never be
 * treated as "no value". The previous `parseInt(index) || parseInt(theme.tier)`
 * fell through on 0 and wrote the PREVIOUS request's tier into `_tier`, which is
 * the attribute that reaches the order and the discount function.
 *
 * @param {string} handle - The channel handle
 * @param {Object} index - The channel → tier index
 * @returns {number} The tier index, 0 when the channel is unknown
 */
export function resolveTier(handle, index) {
  return tierFromTable(handle, normaliseIndex(index));
}

/**
 * Same lookup against an ALREADY normalised table, so `resolveChannel` does not
 * walk and re-allocate the index a second time per resolution.
 * @param {string} handle - The channel handle
 * @param {Object} table - A table whose keys are already lowercase
 * @returns {number}
 */
function tierFromTable(handle, table) {
  const value = table[String(handle).toLowerCase()];
  const tier = typeof value === 'string' ? parseInt(value, 10) : value;
  return Number.isFinite(tier) ? tier : 0;
}

/**
 * Has the attribution gone past its inactivity window?
 * A negative `inactivityMs` means expiry is switched off.
 *
 * @param {Object} params
 * @param {number|string} params.timestamp - When the attribution was ACQUIRED
 * @param {number} params.inactivityMs - The configured expiration window
 * @param {number} params.now - Current time
 * @returns {boolean}
 */
export function isExpired({ timestamp, inactivityMs, now } = {}) {
  if (!timestamp) return false;
  if (!(inactivityMs >= 0)) return false;
  const acquiredAt = new Date(timestamp).getTime();
  if (!Number.isFinite(acquiredAt)) return false;
  return now > acquiredAt + inactivityMs;
}

/**
 * Decide which channel this pageview belongs to.
 *
 * @param {Object} params
 * @param {string|null} params.param - The `?ch=` value, already mapped to '_'
 *   when it names the default channel. Null on an untagged pageview.
 * @param {Object|null} params.stored - The channel entry from local storage
 * @param {Object} params.index - The channel → tier index
 * @param {string} params.defaultChannel - Handle of the shop's default channel
 * @param {number} params.now - Current time
 * @param {number} params.inactivityMs - The configured expiration window
 * @returns {{handle: string, index: number, timestamp: number, source: string}|null}
 */
export function resolveChannel({
  param = null,
  stored = null,
  index = {},
  defaultChannel = 'seo',
  now,
  inactivityMs = -1,
} = {}) {
  const table = normaliseIndex(index);

  // A `?ch=` tag ACQUIRES the channel, and is the ONLY thing that stamps the
  // attribution clock. An unknown channel falls back to the default rather than
  // being written through, so the cart attribute always names a channel the
  // server can price — `price.liquid` looks the handle up in this same index.
  if (param) {
    let handle = String(param).toLowerCase();
    if (handle === String(defaultChannel).toLowerCase()) handle = '_';
    if (!Object.prototype.hasOwnProperty.call(table, handle)) handle = '_';
    return { handle, index: tierFromTable(handle, table), timestamp: now, source: 'param' };
  }

  // Untagged pageview: carry the stored attribution WITHOUT renewing its clock.
  //
  // Renewing it here was fault 2. The expiry is documented as "how long the
  // channel stays active after the user's last interaction", and re-stamping on
  // every pageview turned that into "as long as the visitor keeps browsing" —
  // so an organic visitor who once clicked a CRM link kept the CRM tier
  // indefinitely, and only a full cookie + storage wipe cleared it.
  if (!stored || !stored.handle) return null;
  if (isExpired({ timestamp: stored.timestamp, inactivityMs, now })) return null;

  let handle = String(stored.handle).toLowerCase();
  if (!Object.prototype.hasOwnProperty.call(table, handle)) handle = '_';
  return {
    handle,
    index: tierFromTable(handle, table),
    timestamp: stored.timestamp,
    source: 'stored',
  };
}
